'use strict';

import { assignDeep } from '../../../settings';
import { Effect } from './Effect';
import {ShaderPass} from 'three/examples/jsm/postprocessing/ShaderPass';
import vertex from './Shaders/DOF/vertex.glsl';
import fragment from './Shaders/DOF/fragment.glsl';
import {
  Color,
  ShaderMaterial,
  UniformsUtils,
  WebGLRenderTarget,
} from 'three';
import { CopyShader } from 'three/examples/jsm/shaders/CopyShader';

const DEFAULT_PARAMS = {
  aperture: 0.025,
  maxblur: 0.01
};

export class DOF extends Effect{
  static scope = null;
  constructor (camera,params = {}) {
    super();
    const settings = assignDeep(DEFAULT_PARAMS,params);
    this.camera = camera;
    const material = new ShaderMaterial({
      vertexShader: vertex,
      fragmentShader: fragment,
      uniforms: {
        tDiffuse  : { value: null   },
        tDepth  : { value: null   },
        focus   : { value: 1.0    },
        aspect  : { value: this.camera.aspect },
        aperture: { value: settings.aperture },
        maxblur : { value: settings.maxblur },
        far     : { value: this.camera.far },
        near     : { value: this.camera.near },
      }
    });
    this.effect = new DOFLite(material,window.innerWidth,window.innerHeight);
    DOF.scope = this;
  }

  set config(conf) {
    this.effect.uniforms.aperture.value = conf.aperture;
    this.effect.uniforms.maxblur.value = conf.maxblur;
    this.active = conf.hasOwnProperty('enabled') ? conf.enabled : conf.active;
  }

  updateFocus() {
    this.effect.uniforms.focus.value = this.camera.position.length();
  }

  static updateFocus() {
    if (DOF.scope && DOF.scope.active) {
     DOF.scope.updateFocus();
    }
  }

}

class DOFLite extends ShaderPass{
  constructor (material,width,height) {
    super(material);

    this.defaultRt = new WebGLRenderTarget(width,height);
    this.originalClearColor = new Color();
    this.copyMaterial = new ShaderMaterial( {
      uniforms: UniformsUtils.clone( CopyShader.uniforms ),
      vertexShader: CopyShader.vertexShader,
      fragmentShader: CopyShader.fragmentShader,
      transparent: true,
      depthTest: false,
      depthWrite: false
    });

  }

  render (renderer, writeBuffer, readBuffer) {
    if ( this.uniforms[ this.textureID ] ) {
      this.uniforms[ this.textureID ].value = readBuffer.texture;
      this.uniforms.tDepth.value = readBuffer.depthTexture;
    }

    this.renderPass(renderer,this.material,this.defaultRt);
    this.copyMaterial.uniforms.tDiffuse.value = this.defaultRt.texture;
    this.fsQuad.material = this.copyMaterial;

    if ( this.renderToScreen ) {

      renderer.setRenderTarget( null );
      this.fsQuad.render( renderer );

    } else {

      renderer.setRenderTarget( writeBuffer );
      // TODO: Avoid using autoClear properties, see https://github.com/mrdoob/three.js/pull/15571#issuecomment-465669600
      if ( this.clear ) renderer.clear( renderer.autoClearColor, renderer.autoClearDepth, renderer.autoClearStencil );
      this.fsQuad.render( renderer );

    }
  }

  renderPass( renderer, passMaterial, renderTarget, clearColor, clearAlpha ) {
    // save original state
    renderer.getClearColor( this.originalClearColor );
    const originalClearAlpha = renderer.getClearAlpha();
    const originalAutoClear = renderer.autoClear;

    renderer.setRenderTarget( renderTarget );

    // setup pass state
    renderer.autoClear = false;
    if ( ( clearColor !== undefined ) && ( clearColor !== null ) ) {

      renderer.setClearColor( clearColor );
      renderer.setClearAlpha( clearAlpha || 0.0 );
      renderer.clear();

    }

    this.fsQuad.material = passMaterial;
    this.fsQuad.render( renderer );

    // restore original state
    renderer.autoClear = originalAutoClear;
    renderer.setClearColor( this.originalClearColor );
    renderer.setClearAlpha( originalClearAlpha );

  }

}
